import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { clearAuth } from '@/containers/Login/AuthSlice'
import { ipNotAllowedActions } from '@/shared/tools/ipNotAllowed'
import { isJsonResponse, nonJsonResponseError } from '@/shared/tools/nonJsonResponseError'
import * as Sentry from '@sentry/react'

const useHttp = () => {
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { authTokenSession: token } = useSelector(selectAuthData)
  const dispatch = useDispatch()

  const getConfig = (method, headers = {}) => {
    return {
      headers: {
        Accept: 'Application/json',
        Authorization: 'Bearer ' + token,
        ...headers,
      },
      method,
      mode: 'cors',
    }
  }
  const sendRequest = useCallback(async (url, config = {}, callback, responseType = 'json') => {
    setIsLoading(true)
    try {
      const response = await fetch(url, config)

      if (response.status === 403) {
        const forbiddenData = await response
          .clone()
          .json()
          .catch(() => null)
        const ipRefusalActions = ipNotAllowedActions(response.status, forbiddenData)
        if (ipRefusalActions !== null) {
          ipRefusalActions.forEach(action => dispatch(action))
          setIsLoading(false)
          return
        }
      }

      if (response.status === 401) {
        let data = {}
        try {
          data = await response.json()
        } catch {
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error('Failed to parse JSON from 401 response'), {
              extra: {
                url,
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                timestamp: new Date().toISOString(),
              },
            })
          }
        }
        const errorMessage = data.message || data.error || "Erreur d'autorisation. Veuillez vous reconnecter."
        alert(errorMessage)
        dispatch(clearAuth())
        window.location.href = '/login'
        return
      }

      let data
      const contentType = response.headers.get('content-type') || ''
      if (responseType === 'blob' || contentType.includes('text/csv')) {
        data = await response.blob()
      } else if (response.status === 204) {
        data = {}
      } else if (!isJsonResponse(contentType)) {
        throw nonJsonResponseError(response, contentType)
      } else {
        data = await response.json()
      }

      setIsLoading(false)
      return callback(data, response.status, response)
    } catch (error) {
      if (import.meta.env.PROD) {
        Sentry.captureException(error, { extra: { context: 'useHttp' } })
      }
      setError(error.message || 'An error occurred')
      setIsLoading(false)
    }
  }, [])

  return {
    error,
    isLoading,
    sendRequest,
    getConfig,
  }
}

export default useHttp
